import { ReactNode } from "react";
import { Chart as ChartJS, Filler, LineElement, PointElement, ChartOptions, Plugin, ScriptableContext } from "chart.js";
import { Line } from "react-chartjs-2";
import styles from "./AnalyticsLineChart.module.scss";
import AnalyticsCard from "../card/AnalyticsCard";
import AnalyticsHeader from "../header/AnalyticsHeader";
import AnalyticsState from "../state/AnalyticsState";

// index.tsx registers only the bar pieces. These three are what a filled line needs,
// and registering is idempotent, so every analytics line chart is covered from here.
ChartJS.register(PointElement, LineElement, Filler);

/**
 * Eight slots, assigned in this fixed order and never cycled — a ninth series has
 * no colour by design and belongs in an "Other" bucket or a second chart instead.
 * Validated as a set on a light surface: worst neighbouring pair separates by
 * ΔE 9.1 under protanopia and 19.6 to normal vision. Aqua, yellow and magenta sit
 * just under 3:1 against white, so a chart that reaches those slots owes the
 * reader visible labels rather than colour alone.
 * The portal is light-only (AdminGuard forces it), so there is no dark set.
 */
const SERIES_COLORS = ["#2A78D6", "#EB6834", "#1BAF7A", "#EDA100", "#E87BA4", "#008300", "#4A3AA7", "#E34948"];

// Chart.js paints to canvas and so cannot read the CSS tokens. These mirror
// $text-light-gray, $border-color and $text-dark.
const MUTED_INK = "#17171799";
const GRID = "#E6E8F0";
const INK = "#1A1D1F";

const CHART_SURFACE = "#ffffff";

// The de-emphasis grey a baseline series is drawn in — deliberately outside the
// categorical palette, so it never reads as "one of the series".
const MUTED_SERIES = "#B9BDC7";

/** The wash under a line: the series hue fading out before it reaches the baseline. */
const areaFill = (color: string) => (context: ScriptableContext<"line">) => {
	const { ctx, chartArea } = context.chart;
	// Absent on the first render, before the area has been measured.
	if (!chartArea) return "transparent";

	const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
	gradient.addColorStop(0, `${color}00`);
	gradient.addColorStop(1, `${color}26`);
	return gradient;
};

/** Readers aim at a date, not at a 2px line — Chart.js has no crosshair of its own. */
const crosshair: Plugin<"line"> = {
	id: "analyticsCrosshair",
	afterDatasetsDraw(chart) {
		const [active] = chart.getActiveElements();
		if (!active) return;

		const { ctx, chartArea } = chart;
		ctx.save();
		ctx.beginPath();
		ctx.moveTo(active.element.x, chartArea.top);
		ctx.lineTo(active.element.x, chartArea.bottom);
		ctx.lineWidth = 1;
		ctx.strokeStyle = GRID;
		ctx.stroke();
		ctx.restore();
	},
};

export interface AnalyticsSeries {
	label: string;
	data: number[];
	/**
	 * Which palette slot the series owns, when the caller can drop series from the
	 * set — colour has to follow the entity, so a filter that hides Android must not
	 * repaint iOS. Defaults to the position in the array.
	 */
	colorSlot?: number;
	/**
	 * The series is context rather than the subject — a baseline to compare the others
	 * against. Drawn grey and dashed, so the eye goes to the coloured line and the
	 * difference between them is still legible without colour.
	 */
	muted?: boolean;
}

type Props = {
	title: string;
	/** What the filters currently scope the chart to, e.g. "By platform · last 30 days". */
	subtitle?: string;
	/** One per point. Only the first, middle and last are drawn on the axis. */
	labels: string[];
	/** Up to eight — the palette has eight slots and is never cycled. */
	series: AnalyticsSeries[];
	/**
	 * Washes the area under each line. Area encodes volume and reads cleanly for one
	 * series; leave it off where the point is to compare lines, since overlapping
	 * washes muddy each other.
	 */
	filled?: boolean;
	/** The series are stand-ins rather than measurements — see AnalyticsHeader. */
	mock?: boolean;
	/** Both default to false, for a chart plotted from data that is already in hand. */
	loading?: boolean;
	error?: boolean;
	/** The query's refetch and whether one is already running — see AnalyticsState. */
	onRetry?: () => void;
	retrying?: boolean;
	/** Rendered under the plot, e.g. the finding the chart is evidence for. */
	children?: ReactNode;
};

/** A metric over time, split into up to eight series. */
const AnalyticsLineChart = ({
	title,
	subtitle,
	labels,
	series,
	filled = true,
	mock = false,
	loading = false,
	error = false,
	onRetry,
	retrying,
	children,
}: Readonly<Props>) => {
	const middle = Math.floor((labels.length - 1) / 2);
	const colorOf = (item: AnalyticsSeries, index: number) =>
		item.muted ? MUTED_SERIES : SERIES_COLORS[item.colorSlot ?? index];

	const data = {
		labels,
		datasets: series.map((item, index) => ({
			label: item.label,
			data: item.data,
			borderColor: colorOf(item, index),
			backgroundColor: areaFill(colorOf(item, index)),
			borderWidth: 2,
			borderDash: item.muted ? [6, 4] : undefined,
			fill: filled,
			tension: 0,
			pointRadius: 0,
			pointHoverRadius: 4,
			// The ring keeps the dot legible where it lands on another series' line.
			pointHoverBorderWidth: 2,
			pointHoverBorderColor: CHART_SURFACE,
			pointHoverBackgroundColor: colorOf(item, index),
		})),
	};

	const options: ChartOptions<"line"> = {
		maintainAspectRatio: false,
		// One tooltip listing every series, so the pointer never has to find a line.
		interaction: { mode: "index", intersect: false },
		plugins: {
			// The chart's own legend is rendered as HTML above, in text colours.
			legend: { display: false },
			tooltip: {
				backgroundColor: INK,
				padding: 12,
				cornerRadius: 8,
				displayColors: true,
				boxWidth: 8,
				boxHeight: 8,
				usePointStyle: true,
			},
		},
		scales: {
			x: {
				grid: { display: false },
				border: { display: false },
				ticks: {
					color: MUTED_INK,
					// The design labels the ends and the midpoint only.
					callback: (_, index) => (index === 0 || index === middle || index === labels.length - 1 ? labels[index] : ""),
					maxRotation: 0,
					autoSkip: false,
				},
			},
			y: {
				beginAtZero: true,
				grid: { color: GRID },
				border: { display: false },
				ticks: {
					color: MUTED_INK,
					maxTicksLimit: 5,
					callback: value => Number(value).toLocaleString(),
				},
			},
		},
	};

	return (
		<AnalyticsCard>
			<AnalyticsHeader title={title} subtitle={subtitle} mock={mock}>
				{/* One series needs no legend — the title already names what is plotted. */}
				{series.length > 1 && (
					<ul className={styles.legend}>
						{series.map((item, index) => (
							<li key={item.label} className={styles.legendItem}>
								<span className={styles.legendDot} style={{ backgroundColor: colorOf(item, index) }} />
								{item.label}
							</li>
						))}
					</ul>
				)}
			</AnalyticsHeader>
			{/* Outside the plot rather than inside it: .plot is a fixed 260px, and a
			    spinner stranded at the top of an empty box of that height reads as broken. */}
			{/* No points is the empty state — an axis with nothing plotted on it is worse
			    than a sentence saying there is nothing to plot. */}
			<AnalyticsState
				loading={loading}
				error={error}
				onRetry={onRetry}
				retrying={retrying}
				empty={labels.length === 0}
			>
				<div className={styles.plot}>
					<Line data={data} options={options} plugins={[crosshair]} />
				</div>
			</AnalyticsState>
			{children}
		</AnalyticsCard>
	);
};

export default AnalyticsLineChart;
